/**
 * Doserne — rich "Contract Generator PRO" pop-out modal.
 *
 * Opened from a colleague row in DoserneContracts. Fetches positions
 * (rate + responsibilities + limitations) from contract-settings, prefills
 * the employee's details from their profile, lets the admin fine-tune the
 * contract (position, salary, career path, custom tasks, up to 3 languages)
 * and sends it to the backend generate-html flow (PDF + push + email + link).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, FilePlus, Sparkles, Plus, Trash2, ChevronDown, Eye, PenLine, Check, Download } from "lucide-react";
import { buildContractHtml, COMPANY_SETTINGS } from "./doserneContractTemplate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const L = {
  ro: {
    title: "Generator contract PRO", title_new: "Angajat nou + contract", for: "Pentru", cancel: "Anulează",
    new_emp_hint: "Angajat nou — completează email + date, generezi contractul; primește pe email datele de logare pe Academy + contractul de semnat.",
    ok_new: "Cont creat + email logare + contract trimis 📄",
    langs: "Limbi contract", position: "Poziție", salary: "Salariu (NOK/oră)",
    lang_note: "🇳🇴 Norvegiana e mereu inclusă (versiunea oficială semnată). Angajatul semnează o singură dată tot documentul.",
    section_emp: "Date angajat", name: "Nume complet", pid: "CNP / Person ID", address: "Adresă",
    phone: "Telefon", email: "E-mail", bank: "Cont bancar",
    section_contract: "Detalii contract", ctype: "Tip contract", start: "Data începerii",
    end: "Data expirării", place: "Loc de muncă", hours: "Program (ore)", payday: "Data plății",
    ot_wd: "Overtime zi lucrătoare", ot_sp: "Overtime dum./sărbători",
    career: "Include career path (responsabilități cumulative)", lower: "Responsabilități moștenite (nivele inferioare)",
    custom: "Sarcini personalizate", add_task: "Adaugă sarcină…", notes: "Note / clauze suplimentare",
    generate: "Generează & trimite", generating: "Se generează…",
    ok: "Contract generat + notificare & email trimise 📄", fail: "Eroare la generare",
    perm: "Permanent", temp: "Temporar", proj: "Proiect", no_pos: "Nicio poziție disponibilă",
    preview: "Previzualizează", preview_title: "Previzualizare contract", close: "Închide", download: "Descarcă PDF",
    emp_sig: "Semnătura ta (angajator)", emp_sig_auto: "se pune automat pe contract",
    emp_sig_none: "Nu ai o semnătură salvată. Scrie numele tău ca semnătură:",
    emp_sig_save: "Salvează semnătura", emp_sig_saved: "Semnătură salvată ✓", sig_name_ph: "Numele tău complet",
    change: "Schimbă",
    msg_lang: "Limba mesajului către angajat", msg_lang_note: "Emailul de logare + linkul de semnare + notificarea push se trimit în această limbă.",
    emp_sig_kurs: "(preluată din semnătura ta de la cursuri)",
  },
  no: {
    title: "Kontraktgenerator PRO", title_new: "Ny ansatt + kontrakt", for: "For", cancel: "Avbryt",
    new_emp_hint: "Ny ansatt — fyll inn e-post + detaljer og generer kontrakten; de får innloggingsdetaljer til Academy + kontrakten på e-post.",
    ok_new: "Konto opprettet + innlogging + kontrakt sendt 📄",
    langs: "Kontraktspråk", position: "Stilling", salary: "Lønn (NOK/time)",
    lang_note: "🇳🇴 Norsk er alltid inkludert (offisiell signert versjon). Den ansatte signerer hele dokumentet én gang.",
    section_emp: "Ansattdata", name: "Fullt navn", pid: "Person ID", address: "Adresse",
    phone: "Telefon", email: "E-post", bank: "Bankkonto",
    section_contract: "Kontraktdetaljer", ctype: "Kontrakttype", start: "Startdato",
    end: "Utløpsdato", place: "Arbeidssted", hours: "Arbeidstid", payday: "Utbetalingsdato",
    ot_wd: "Overtid hverdag", ot_sp: "Overtid søn./helligdag",
    career: "Inkluder karrierevei (kumulative ansvar)", lower: "Arvede ansvar (lavere nivåer)",
    custom: "Egendefinerte oppgaver", add_task: "Legg til oppgave…", notes: "Notater / tilleggsvilkår",
    generate: "Generer & send", generating: "Genererer…",
    ok: "Kontrakt generert + varsel & e-post sendt 📄", fail: "Feil ved generering",
    perm: "Fast", temp: "Midlertidig", proj: "Prosjekt", no_pos: "Ingen stillinger tilgjengelig",
    preview: "Forhåndsvis", preview_title: "Forhåndsvisning kontrakt", close: "Lukk", download: "Last ned PDF",
    emp_sig: "Din signatur (arbeidsgiver)", emp_sig_auto: "stemples automatisk på kontrakten",
    emp_sig_none: "Du har ingen lagret signatur. Skriv navnet ditt som signatur:",
    emp_sig_save: "Lagre signatur", emp_sig_saved: "Signatur lagret ✓", sig_name_ph: "Ditt fulle navn",
    change: "Endre",
    msg_lang: "Språk for melding til ansatt", msg_lang_note: "Innloggings-e-post + signeringslenke + push-varsel sendes på dette språket.",
    emp_sig_kurs: "(hentet fra signaturen din fra kurs)",
  },
  en: {
    title: "Contract Generator PRO", title_new: "New employee + contract", for: "For", cancel: "Cancel",
    new_emp_hint: "New employee — fill in email + details and generate the contract; they get Academy login details + the contract to sign by email.",
    ok_new: "Account created + login + contract sent 📄",
    langs: "Contract languages", position: "Position", salary: "Salary (NOK/hour)",
    lang_note: "🇳🇴 Norwegian is always included (official signed version). The employee signs the whole document once.",
    section_emp: "Employee data", name: "Full name", pid: "Personal ID", address: "Address",
    phone: "Phone", email: "Email", bank: "Bank account",
    section_contract: "Contract details", ctype: "Contract type", start: "Start date",
    end: "Expiry date", place: "Place of work", hours: "Working hours", payday: "Payment date",
    ot_wd: "Overtime weekday", ot_sp: "Overtime sun./holidays",
    career: "Include career path (cumulative responsibilities)", lower: "Inherited responsibilities (lower levels)",
    custom: "Custom tasks", add_task: "Add task…", notes: "Notes / extra terms",
    generate: "Generate & send", generating: "Generating…",
    ok: "Contract generated + notification & email sent 📄", fail: "Generation error",
    perm: "Permanent", temp: "Temporary", proj: "Project", no_pos: "No positions available",
    preview: "Preview", preview_title: "Contract preview", close: "Close", download: "Download PDF",
    emp_sig: "Your signature (employer)", emp_sig_auto: "auto-stamped on the contract",
    emp_sig_none: "You have no saved signature. Type your name as signature:",
    emp_sig_save: "Save signature", emp_sig_saved: "Signature saved ✓", sig_name_ph: "Your full name",
    change: "Change",
    msg_lang: "Language of message to employee", msg_lang_note: "The login email + signing link + push notification are sent in this language.",
  },
};

const inputCls = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-200";
const labelCls = "block text-[11px] font-bold text-slate-500 uppercase mb-1";

export default function DoserneGenerateModal({ lang = "no", member, authHeader, onClose, onDone, newEmployee = false }) {
  const t = useMemo(() => L[lang] || L.no, [lang]);
  const [positions, setPositions] = useState([]);
  const [loadingPos, setLoadingPos] = useState(true);
  const [busy, setBusy] = useState(false);
  const [langs, setLangs] = useState(["no", "ro", "en"]);
  const [notifyLang, setNotifyLang] = useState((member?.doserne_lang || lang || "no").toLowerCase());
  const [showCumulative, setShowCumulative] = useState(false);
  const [selectedLowerResp, setSelectedLowerResp] = useState({});
  const [newTask, setNewTask] = useState("");
  const [empSig, setEmpSig] = useState({ signature_url: "", signature_text: "", signature_type: "", has_signature: false });
  const [sigNameDraft, setSigNameDraft] = useState("");
  const [savingSig, setSavingSig] = useState(false);
  const [editingSig, setEditingSig] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [dlBusy, setDlBusy] = useState(false);

  const [cd, setCd] = useState({
    employeeId: member?.email || "",
    contractType: "Permanent",
    empName: member?.full_name || "",
    empId: "",
    empAddress: "",
    empPhone: "",
    empEmail: member?.email || "",
    empBankAccount: "",
    startDate: "",
    endDate: "",
    contractDate: new Date().toISOString().split("T")[0],
    positionId: "",
    hourlyRate: 240,
    otWeekday: "50%",
    otSpecial: "100%",
    workHours: "07:00–15:00",
    placeOfWork: "Oslo and surrounding areas (East & South). Work elsewhere in Norway may be required.",
    payDate: "5th of each month (weekend: nearest business day)",
    notes: "",
    customTasks: [],
  });
  const set = (k, v) => setCd((p) => ({ ...p, [k]: v }));

  // Fetch positions + prefill full employee profile.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/contract-settings/positions`, authHeader());
        const list = Array.isArray(data) ? data : [];
        setPositions(list);
        const def = list.find((p) => p.id === "scaffold_l2") || list[0];
        if (def) setCd((p) => ({ ...p, positionId: def.id, hourlyRate: def.rate || p.hourlyRate }));
      } catch { /* silent */ }
      setLoadingPos(false);
      if (member?.email) {
        try {
          const { data } = await axios.get(`${API}/doserne/admin/users/${encodeURIComponent(member.email)}`, authHeader());
          const u = data?.user || {};
          setCd((p) => ({
            ...p,
            empName: u.full_name || p.empName,
            empId: u.personnummer || p.empId,
            empAddress: u.address || p.empAddress,
            empPhone: u.phone || p.empPhone,
            empBankAccount: u.bank_account || p.empBankAccount,
          }));
        } catch { /* silent */ }
      }
      try {
        const { data } = await axios.get(`${API}/doserne/me/employer-signature`, authHeader());
        setEmpSig(data || {});
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const employerSignature = useMemo(() => {
    if (empSig.signature_type === "drawn" && empSig.signature_url) return { type: "drawn", url: empSig.signature_url };
    if (empSig.signature_type === "typed" && empSig.signature_text) return { type: "typed", text: empSig.signature_text };
    return null;
  }, [empSig]);

  const makeHtml = () => buildContractHtml({
    contractData: cd, pos: currentPos, positions, showCumulative, selectedLowerResp,
    langs: ["no", ...langs.filter((l) => l !== "no")],
    settings: COMPANY_SETTINGS, employerSignature,
  });

  // One HTML document per language (used for the inline signing view + switcher).
  const makeHtmlByLang = () => {
    const finalLangs = ["no", ...langs.filter((l) => l !== "no")];
    const map = {};
    finalLangs.forEach((lg) => {
      map[lg] = buildContractHtml({
        contractData: cd, pos: currentPos, positions, showCumulative, selectedLowerResp,
        langs: [lg], settings: COMPANY_SETTINGS, employerSignature,
      });
    });
    return { finalLangs, map };
  };

  const openPreview = () => {
    if (!currentPos) { toast.error(t.no_pos); return; }
    setPreviewHtml(makeHtml());
  };

  const downloadPdf = async () => {
    const html = previewHtml || makeHtml();
    const safeName = (cd.empName || "contract").replace(/[^a-zA-Z0-9_-]/g, "_");
    const filename = `Kontrakt_${safeName}.pdf`;
    setDlBusy(true);
    try {
      const res = await axios.post(
        `${API}/doserne/admin/contracts/preview-pdf`,
        { html, filename },
        { ...authHeader(), responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t.fail);
    }
    setDlBusy(false);
  };

  const saveTypedSignature = async () => {
    const name = sigNameDraft.trim();
    if (name.length < 2) return;
    setSavingSig(true);
    try {
      const { data } = await axios.post(`${API}/doserne/me/employer-signature`, { signature_text: name, signature_type: "typed" }, authHeader());
      setEmpSig({ signature_url: "", signature_text: data.signature_text, signature_type: "typed", has_signature: true });
      setEditingSig(false);
    } catch { toast.error(t.fail); }
    setSavingSig(false);
  };

  const currentPos = useMemo(() => positions.find((p) => p.id === cd.positionId) || positions[0], [positions, cd.positionId]);

  const lowerPositions = useMemo(() => {
    if (!currentPos) return [];
    const currentOrder = currentPos.order || 0;
    return positions
      .filter((p) => !p.nonScaffoldProgression && (p.order || 0) > 0 && (p.order || 0) < currentOrder && p.id !== currentPos.id && p.responsibilities)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [positions, currentPos]);

  const toggleLang = (lg) => {
    if (lg === "no") return; // Norwegian is the official version — always included.
    setLangs((prev) => (prev.includes(lg) ? prev.filter((x) => x !== lg) : [...prev, lg]));
  };
  const onPickPosition = (id) => {
    const p = positions.find((x) => x.id === id);
    set("positionId", id);
    if (p?.rate) set("hourlyRate", p.rate);
  };
  const addTask = () => {
    const v = newTask.trim();
    if (!v) return;
    setCd((p) => ({ ...p, customTasks: [...p.customTasks, v] }));
    setNewTask("");
  };
  const removeTask = (i) => setCd((p) => ({ ...p, customTasks: p.customTasks.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!currentPos) { toast.error(t.no_pos); return; }
    if (!cd.empName.trim()) { toast.error(t.name); return; }
    if (!langs.length) { toast.error(t.langs); return; }
    const targetEmail = (newEmployee ? cd.empEmail : member?.email || "").trim().toLowerCase();
    if (!targetEmail || !targetEmail.includes("@")) { toast.error(t.email); return; }
    setBusy(true);
    try {
      if (newEmployee) {
        await axios.post(
          `${API}/doserne/admin/employees/create`,
          {
            email: targetEmail,
            full_name: cd.empName,
            phone: cd.empPhone,
            personnummer: cd.empId,
            address: cd.empAddress,
            doserne_lang: lang,
            platform_access_from: cd.startDate || "",
          },
          authHeader()
        );
      }
      const html = makeHtml();
      const { finalLangs, map } = makeHtmlByLang();
      const safeName = (cd.empName || "contract").replace(/[^a-zA-Z0-9_-]/g, "_");
      await axios.post(
        `${API}/doserne/admin/contracts/generate-html`,
        { email: targetEmail, html, filename: `Kontrakt_${safeName}.pdf`, start_date: cd.startDate || "", langs: finalLangs, html_by_lang: map, notify_lang: notifyLang },
        authHeader()
      );
      toast.success(newEmployee ? t.ok_new : t.ok);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.fail);
    }
    setBusy(false);
  };

  const langBtns = [
    { id: "no", label: "🇳🇴 NO" },
    { id: "ro", label: "🇷🇴 RO" },
    { id: "en", label: "🇬🇧 EN" },
  ];
  const ctypes = [
    { id: "Permanent", label: t.perm },
    { id: "Temporary", label: t.temp },
    { id: "Project", label: t.proj },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="pro-gen-modal">
      <div className="bg-[#fafaf7] w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white flex-shrink-0">
          <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-sky-600" /> {newEmployee ? t.title_new : t.title}
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="pro-gen-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-5" style={{ display: previewHtml !== null ? "none" : undefined }}>
          <div className="text-xs text-slate-500">
            {newEmployee ? (
              <span className="text-emerald-700 font-bold">{t.new_emp_hint}</span>
            ) : (
              <>{t.for}: <b className="text-slate-800">{member?.full_name}</b> · {member?.email}</>
            )}
          </div>

          {/* Languages */}
          <div>
            <label className={labelCls}>{t.langs}</label>
            <div className="grid grid-cols-3 gap-2">
              {langBtns.map((lg) => {
                const on = lg.id === "no" ? true : langs.includes(lg.id);
                const locked = lg.id === "no";
                return (
                  <button key={lg.id} type="button" onClick={() => toggleLang(lg.id)}
                    className={`px-3 py-2 rounded-lg text-sm font-bold border-2 relative ${on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-500 bg-white"} ${locked ? "cursor-default" : ""}`}
                    data-testid={`pro-lang-${lg.id}`}>
                    {lg.label}{locked ? " 🔒" : ""}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">{t.lang_note}</p>
          </div>

          {/* Message language (email + push to employee) */}
          <div>
            <label className={labelCls}>{t.msg_lang}</label>
            <div className="grid grid-cols-3 gap-2">
              {langBtns.map((lg) => {
                const on = notifyLang === lg.id;
                return (
                  <button key={lg.id} type="button" onClick={() => setNotifyLang(lg.id)}
                    className={`px-3 py-2 rounded-lg text-sm font-bold border-2 ${on ? "border-sky-600 bg-sky-600 text-white" : "border-slate-200 text-slate-500 bg-white"}`}
                    data-testid={`pro-notify-lang-${lg.id}`}>
                    {lg.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">{t.msg_lang_note}</p>
          </div>

          {/* Employer signature (auto-stamped) */}
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="pro-empsig">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] font-black text-sky-700 uppercase tracking-wide flex items-center gap-1.5">
                <PenLine className="w-3.5 h-3.5" /> {t.emp_sig}
              </div>
              {empSig.has_signature && !editingSig && (
                <button type="button" onClick={() => { setEditingSig(true); setSigNameDraft(empSig.signature_text || ""); }} className="text-[11px] font-bold text-slate-500 hover:text-slate-800">{t.change}</button>
              )}
            </div>
            {empSig.has_signature && !editingSig ? (
              <div className="mt-2 flex items-center gap-3">
                {empSig.signature_type === "drawn" && empSig.signature_url ? (
                  <img src={empSig.signature_url} alt="signature" className="h-12 object-contain" data-testid="pro-empsig-img" />
                ) : (
                  <span style={{ fontFamily: "'Segoe Script','Brush Script MT','Comic Sans MS',cursive" }} className="text-2xl text-slate-800" data-testid="pro-empsig-text">{empSig.signature_text}</span>
                )}
                <span className="text-[11px] text-emerald-600 font-bold inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {t.emp_sig_auto}</span>
                {empSig.from_kurs && (
                  <span className="text-[11px] text-slate-400" data-testid="pro-empsig-kurs">{t.emp_sig_kurs}</span>
                )}
              </div>
            ) : (
              <div className="mt-2">
                <div className="text-[11px] text-slate-500 mb-1.5">{t.emp_sig_none}</div>
                <div className="flex gap-2">
                  <input className={inputCls} placeholder={t.sig_name_ph} value={sigNameDraft} onChange={(e) => setSigNameDraft(e.target.value)} data-testid="pro-empsig-input" />
                  <button type="button" onClick={saveTypedSignature} disabled={savingSig || sigNameDraft.trim().length < 2} className="px-3 rounded-lg bg-slate-900 text-white text-sm font-bold whitespace-nowrap disabled:bg-slate-300" data-testid="pro-empsig-save">
                    {savingSig ? <Loader2 className="w-4 h-4 animate-spin" /> : t.emp_sig_save}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Position + salary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t.position}</label>
              <div className="relative">
                <select className={`${inputCls} appearance-none pr-8`} value={cd.positionId} onChange={(e) => onPickPosition(e.target.value)} disabled={loadingPos} data-testid="pro-position">
                  {positions.map((p) => (<option key={p.id} value={p.id}>{p.label}</option>))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
            <div>
              <label className={labelCls}>{t.salary}</label>
              <input type="number" className={inputCls} value={cd.hourlyRate} onChange={(e) => set("hourlyRate", e.target.value)} data-testid="pro-salary" />
            </div>
          </div>

          {/* Employee data */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="text-[11px] font-black text-sky-700 uppercase tracking-wide">{t.section_emp}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={labelCls}>{t.name}</label><input className={inputCls} value={cd.empName} onChange={(e) => set("empName", e.target.value)} data-testid="pro-empname" /></div>
              <div><label className={labelCls}>{t.pid}</label><input className={inputCls} value={cd.empId} onChange={(e) => set("empId", e.target.value)} /></div>
              <div><label className={labelCls}>{t.address}</label><input className={inputCls} value={cd.empAddress} onChange={(e) => set("empAddress", e.target.value)} /></div>
              <div><label className={labelCls}>{t.phone}</label><input className={inputCls} value={cd.empPhone} onChange={(e) => set("empPhone", e.target.value)} /></div>
              <div><label className={labelCls}>{t.email}</label><input className={inputCls} value={cd.empEmail} onChange={(e) => set("empEmail", e.target.value)} /></div>
              <div><label className={labelCls}>{t.bank}</label><input className={inputCls} value={cd.empBankAccount} onChange={(e) => set("empBankAccount", e.target.value)} /></div>
            </div>
          </div>

          {/* Contract details */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="text-[11px] font-black text-sky-700 uppercase tracking-wide">{t.section_contract}</div>
            <div>
              <label className={labelCls}>{t.ctype}</label>
              <div className="grid grid-cols-3 gap-2">
                {ctypes.map((c) => (
                  <button key={c.id} type="button" onClick={() => set("contractType", c.id)}
                    className={`px-3 py-2 rounded-lg text-sm font-bold border-2 ${cd.contractType === c.id ? "border-sky-600 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-500"}`}
                    data-testid={`pro-ctype-${c.id}`}>{c.label}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={labelCls}>{t.start}</label><input type="date" className={inputCls} value={cd.startDate} onChange={(e) => set("startDate", e.target.value)} data-testid="pro-startdate" /></div>
              {cd.contractType !== "Permanent" && (
                <div><label className={labelCls}>{t.end}</label><input type="date" className={inputCls} value={cd.endDate} onChange={(e) => set("endDate", e.target.value)} data-testid="pro-enddate" /></div>
              )}
              <div><label className={labelCls}>{t.hours}</label><input className={inputCls} value={cd.workHours} onChange={(e) => set("workHours", e.target.value)} /></div>
              <div><label className={labelCls}>{t.payday}</label><input className={inputCls} value={cd.payDate} onChange={(e) => set("payDate", e.target.value)} /></div>
              <div><label className={labelCls}>{t.ot_wd}</label><input className={inputCls} value={cd.otWeekday} onChange={(e) => set("otWeekday", e.target.value)} /></div>
              <div><label className={labelCls}>{t.ot_sp}</label><input className={inputCls} value={cd.otSpecial} onChange={(e) => set("otSpecial", e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>{t.place}</label><input className={inputCls} value={cd.placeOfWork} onChange={(e) => set("placeOfWork", e.target.value)} /></div>
            </div>
          </div>

          {/* Career path */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="pro-career-toggle">
              <input type="checkbox" checked={showCumulative} onChange={(e) => setShowCumulative(e.target.checked)} className="w-4 h-4 accent-sky-600" />
              <span className="text-sm font-bold text-slate-800">{t.career}</span>
            </label>
            {showCumulative && lowerPositions.length > 0 && (
              <div className="space-y-3 pt-1">
                <div className="text-[11px] font-bold text-slate-500 uppercase">{t.lower}</div>
                {lowerPositions.map((hp) => {
                  const list = hp.responsibilities?.[lang] || hp.responsibilities?.en || [];
                  return (
                    <div key={hp.id} className="border border-slate-100 rounded-lg p-2.5 bg-slate-50">
                      <div className="text-xs font-black text-slate-700 mb-1.5">{hp.label}</div>
                      <div className="space-y-1">
                        {list.map((r, i) => {
                          const key = `${hp.id}:${i}`;
                          const checked = selectedLowerResp[key] !== false;
                          const isHeader = /^\*\*.+\*\*$/.test(r);
                          return (
                            <label key={key} className="flex items-start gap-2 text-[12px] text-slate-600 cursor-pointer">
                              <input type="checkbox" checked={checked} onChange={() => setSelectedLowerResp((prev) => ({ ...prev, [key]: !checked }))} className="mt-0.5 w-3.5 h-3.5 accent-emerald-600" />
                              <span className={isHeader ? "font-bold text-slate-800" : ""}>{r.replace(/^\*\*|\*\*$/g, "")}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Custom tasks */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
            <div className="text-[11px] font-black text-sky-700 uppercase tracking-wide">{t.custom}</div>
            {cd.customTasks.length > 0 && (
              <ul className="space-y-1.5">
                {cd.customTasks.map((tk, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-1.5">
                    <span className="truncate italic">{tk}</span>
                    <button type="button" onClick={() => removeTask(i)} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <input className={inputCls} placeholder={t.add_task} value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }} data-testid="pro-newtask" />
              <button type="button" onClick={addTask} className="px-3 rounded-lg bg-slate-900 text-white flex items-center" data-testid="pro-addtask"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className={labelCls}>{t.notes}</label>
            <textarea className={`${inputCls} min-h-[70px]`} value={cd.notes} onChange={(e) => set("notes", e.target.value)} data-testid="pro-notes" />
          </div>
        </div>

        {/* Inline contract preview — stays inside the same pop-up */}
        {previewHtml !== null && (
          <iframe title="contract-preview" srcDoc={previewHtml} className="flex-1 w-full bg-white border-t border-slate-200 min-h-[65vh]" data-testid="pro-preview-frame" />
        )}

        {/* Footer */}
        <div className="flex gap-2 px-5 py-4 border-t border-slate-200 bg-white flex-shrink-0">
          {previewHtml !== null ? (
            <>
              <button onClick={() => setPreviewHtml(null)} className="py-2.5 px-3 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 inline-flex items-center gap-1.5" data-testid="pro-preview-back">
                <X className="w-4 h-4" /> {t.close}
              </button>
              <button onClick={downloadPdf} disabled={dlBusy} className="py-2.5 px-3 rounded-lg border-2 border-emerald-600 text-emerald-700 text-sm font-black inline-flex items-center justify-center gap-1.5 disabled:opacity-40" data-testid="pro-download-pdf">
                {dlBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t.download}
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} className="py-2.5 px-3 rounded-lg border border-slate-200 text-sm font-bold text-slate-600" data-testid="pro-cancel">{t.cancel}</button>
              <button onClick={openPreview} disabled={loadingPos} className="py-2.5 px-3 rounded-lg border-2 border-slate-900 text-slate-900 text-sm font-black inline-flex items-center justify-center gap-1.5 disabled:opacity-40" data-testid="pro-preview">
                <Eye className="w-4 h-4" /> {t.preview}
              </button>
            </>
          )}
          <button onClick={submit} disabled={busy || loadingPos} className="flex-1 py-2.5 rounded-lg bg-sky-600 text-white text-sm font-black inline-flex items-center justify-center gap-2 disabled:bg-slate-300" data-testid="pro-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FilePlus className="w-4 h-4" />} {busy ? t.generating : t.generate}
          </button>
        </div>
      </div>
    </div>
  );
}
